import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFileStore } from '../stores/file-store';
import FileDetailsViewer from '../components/files/FileDetailsViewer';
import { supabase } from '../config/supabase';
import { KnowledgeFile } from '../types';
import Loader from '../components/shared/Progressbar';

const FileDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { files, addFile } = useFileStore();
  const [isLoading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // 先从 store 中查找
  let file = files.find(f => f.id === id);
  
  // 如果 store 中没有，尝试从数据库获取
  useEffect(() => {
    const fetchFileById = async () => {
      if (file || !id || isLoading || notFound || accessDenied) return;
      
      setIsLoading(true);
      try {
        // 获取当前用户信息
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setAccessDenied(true);
          return;
        }
        
        const { data, error } = await supabase
          .from('files')
          .select(`
            *,
            tags:file_tags(
              tag:tags(*)
            ),
            versions:file_versions(*)
          `)
          .eq('id', id)
          .is('deleted_at', null)
          .single();
        
        if (error) {
          console.error('Error fetching file:', error);
          if (error.code === 'PGRST116') {
            // 没有找到记录
            setNotFound(true);
          }
          return;
        }
        
        if (data) {
          // 检查私密文件权限：如果是其他人的私密文件，拒绝访问
          if (data.owner_only && data.created_by !== session.user.id) {
            console.log('Access denied: Private file belongs to another user');
            setAccessDenied(true);
            return;
          }
          
          // 转换数据格式
          const transformedFile: KnowledgeFile = {
            ...data,
            tags: data.tags?.map((t: any) => t.tag) || [],
            versions: data.versions || [],
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            createdBy: data.created_by,
            updatedBy: data.updated_by
          } as KnowledgeFile;
          
          // 添加到 store
          addFile(transformedFile);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error fetching file by ID:', err);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFileById();
  }, [id, file, isLoading, notFound, accessDenied, addFile]);
  
  // 重新从 store 获取（可能已被添加）
  file = files.find(f => f.id === id);
  
  // 文件未找到或拒绝访问，显示错误并延迟重定向
  useEffect(() => {
    if (notFound || accessDenied) {
      const timer = setTimeout(() => {
        navigate('/uadmin/files');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notFound, accessDenied, navigate]);
  
  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh]">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">无访问权限</h2>
          <p className="text-gray-500 mb-4">该文件为私密文件，仅创建者可查看</p>
          <p className="text-sm text-gray-400">3秒后自动返回文件列表...</p>
        </div>
      </div>
    );
  }
  
  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh]">
        <div className="text-center">
          <div className="text-6xl mb-4">📄</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">文件未找到</h2>
          <p className="text-gray-500 mb-4">该文件可能已被删除或您没有访问权限</p>
          <p className="text-sm text-gray-400">3秒后自动返回文件列表...</p>
        </div>
      </div>
    );
  }
  
  if (isLoading || !file) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Loader />
      </div>
    );
  }
  
  return (
    <div className="h-full">
      <FileDetailsViewer file={file} />
    </div>
  );
};

export default FileDetailsPage;